// In Components/Home/Home.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import {
  getDashboard,
  toggleUserStatus,
  rechargeUser,
  createUser,
  editUser,
  getUserDetail,
  updateSandboxToken,
  updateProductionToken,
  setProductionMode, // mock or real API
  setInvoiceBackup,
  impersonateClient,
  toggleApproval,
  getFbrHsCodes,
  getFbrProvinces,
  getFbrRates,
  getFbrSroItems,
  getFbrSroSchedules,
  getFbrTransactionTypes,
  getFbrUoms,
  getAdminUsers,
} from '../Api/Api';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import PageShell from '../Layout/PageShell';
import { hasPermission, isSuperAdmin, SUPER_ADMIN_EMAIL } from '../Auth/auth';

const CLIENT_PORTAL_URL = process.env.REACT_APP_CLIENT_PORTAL_URL || 'https://dev.konnectfbr.com';
const PROVINCES = [
  'Azad Jammu and Kashmir',
  'Balochistan',
  'Gilgit-Baltistan',
  'Islamabad Capital Territory',
  'Khyber Pakhtunkhwa',
  'Punjab',
  'Sindh',
];
const DEFAULT_SALE_TYPE = 'Goods at standard rate (default)';
const BUSINESS_SCENARIO_IDS = Array.from({ length: 28 }, (_, index) => `SN${String(index + 1).padStart(3, '0')}`);
const BACKUP_OPTIONS = [
  { months: 1, label: '1 month' },
  { months: 6, label: '6 months' },
  { months: 12, label: '1 year' },
  { months: 24, label: '2 years' },
  { months: 36, label: '3 years' },
  { months: 48, label: '4 years' },
  { months: 60, label: '5 years' },
];
const isDefaultSaleType = (value) =>
  !value || String(value).trim().toLowerCase() === DEFAULT_SALE_TYPE.toLowerCase();
const pick = (obj, keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return '';
};
const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const compactText = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
const provinceAliases = {
  'Azad Jammu and Kashmir': ['Azad Jammu and Kashmir', 'AJK'],
  'Gilgit-Baltistan': ['Gilgit-Baltistan', 'Gilgit Baltistan'],
  'Islamabad Capital Territory': ['Islamabad Capital Territory', 'Islamabad'],
  'Khyber Pakhtunkhwa': ['Khyber Pakhtunkhwa', 'KPK', 'KP'],
};
const provinceMatches = (fbrProvince, formProvince) => {
  const desc = getProvinceDesc(fbrProvince);
  const aliases = provinceAliases[formProvince] || [formProvince];
  return aliases.some((alias) => {
    const left = compactText(desc);
    const right = compactText(alias);
    return left === right || left.includes(right) || right.includes(left);
  });
};
const getUomDesc = (opt) => pick(opt, ['description', 'uoM_DESC', 'UOM_DESC', 'uom_desc', 'uomDescription', 'uom', 'uoM', 'UOM']);
const getSaleTypeId = (opt) => pick(opt, ['transactioN_TYPE_ID', 'TRANSACTION_TYPE_ID', 'transaction_type_id', 'transactionTypeId', 'transTypeId', 'id']);
const getSaleTypeDesc = (opt) => pick(opt, ['transactioN_DESC', 'TRANSACTION_DESC', 'transaction_desc', 'transactionDesc', 'description', 'desc']);
const getRateId = (opt) => pick(opt, ['ratE_ID', 'RATE_ID', 'rateId', 'rate_id', 'id']);
const getRateDesc = (opt) => pick(opt, ['ratE_DESC', 'RATE_DESC', 'rateDesc', 'rate_desc', 'rate', 'description', 'desc']);
const getSroId = (opt) => pick(opt, ['srO_ID', 'SRO_ID', 'sroId', 'sro_id', 'id']);
const getSroDesc = (opt) => pick(opt, ['srO_DESC', 'SRO_DESC', 'sroDesc', 'sro_desc', 'sroScheduleNo', 'description', 'desc']);
const getSroItemId = (opt) => pick(opt, ['srO_ITEM_ID', 'SRO_ITEM_ID', 'sroItemId', 'sro_item_id', 'id']);
const getSroItemDesc = (opt) => pick(opt, ['srO_ITEM_DESC', 'SRO_ITEM_DESC', 'sroItemDesc', 'sro_item_desc', 'sroItemSerialNo', 'description', 'desc']);
const getProvinceCode = (opt) => pick(opt, ['stateProvinceCode', 'statE_PROVINCE_CODE', 'STATE_PROVINCE_CODE', 'state_province_code', 'provinceCode', 'province_code', 'code']);
const getProvinceDesc = (opt) => pick(opt, ['stateProvinceDesc', 'statE_PROVINCE_DESC', 'STATE_PROVINCE_DESC', 'state_province_desc', 'provinceDesc', 'province_desc', 'description', 'desc']);
const getHsCodeValue = (opt) => pick(opt, ['hS_CODE', 'HS_CODE', 'hs_code', 'itemCode', 'item_code', 'code']);
const getHsCodeDesc = (opt) => pick(opt, ['description', 'hS_DESC', 'HS_DESC', 'hs_desc', 'itemDescription', 'item_desc', 'desc']);
const normalizeHsCode = (value) => {
  const text = String(value || '').trim();
  const match = text.match(/\b\d{4}\.\d{3,4}\b/);
  return match ? match[0] : text;
};
const getScenarioValue = (row) => typeof row === 'string' ? row : pick(row, ['scenario_id', 'scenarioId', 'id', 'code', 'value']);
const getHsRowValue = (row, keys) => typeof row === 'string' ? row : pick(row, keys);
const unwrapList = (res) => {
  const response = res?.data?.response;
  if (Array.isArray(response?.detail)) return response.detail;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  if (Array.isArray(res?.data?.detail)) return res.data.detail;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};
const getAssignedUserIds = (row) => {
  const assigned =
    row?.assigned_user_ids ??
    row?.assigned_users ??
    row?.assigned_admin_ids ??
    row?.assigned_admins ??
    row?.admin_user_ids ??
    [];
  if (!Array.isArray(assigned)) return [];
  return assigned
    .map((item) => item?.admin_id ?? item?.user_id ?? item?.id ?? item)
    .filter((id) => id !== undefined && id !== null)
    .map(String);
};
const mapAssignableAdmin = (admin) => ({
  id: admin.admin_id ?? admin.id ?? admin.user_id,
  name: admin.name ?? admin.full_name ?? admin.admin_name ?? '',
  email: admin.email ?? '',
  status: admin.status ?? (admin.is_active === 0 ? 'Inactive' : 'Active'),
});

const mapDashboardUser = (u) => ({
  id: u.user_id,
  company: u.company_name || 'Unknown',
  name: u.full_name || 'N/A',
  email: u.email || 'N/A',
  status: u.is_active ? 'Active' : 'Inactive',
  expiry_date: u.expiry_date ? new Date(u.expiry_date).toISOString().split('T')[0] : 'N/A',
  sandbox_token: u.token_dev || u.sandbox_token || '',
  production_token: u.token_prod || u.production_token || '',
  is_prod_token: typeof u.is_prod_token === 'number' ? u.is_prod_token : (u.is_prod_token ? 1 : 0),
  is_approval: typeof u.is_approval === 'number' ? u.is_approval : (u.is_approval ? 1 : 0),
  is_invoice_history: typeof u.is_invoice_history === 'number' ? u.is_invoice_history : (u.is_invoice_history ? 1 : 0),
  invoice_history_months: Number(u.invoice_history_months || u.backup_months || u.invoice_backup_months || (u.is_invoice_history ? 60 : 1)),
  client_role: u.client_role || 'admin',
  parent_user_id: u.parent_user_id || null,
  requires_invoice_approval: typeof u.requires_invoice_approval === 'number' ? u.requires_invoice_approval : (u.requires_invoice_approval ? 1 : 0),
  admin_code: u.admin_code || '',
  ntn_cnic: u.ntn_cnic || '',
  sales_tax_id: u.sales_tax_id || '',
  gst_number: u.gst_number || '',
  province: u.province || '',
  city: u.city || '',
  business_address: u.business_address || '',
  contact_person: u.contact_person || '',
  phone: u.phone || '',
  business_scenarios: Array.isArray(u.business_scenarios) ? u.business_scenarios : [],
  hs_codes: Array.isArray(u.hs_codes) ? u.hs_codes : [],
  assigned_user_ids: getAssignedUserIds(u),
});

const Home = () => {
  const navigate = useNavigate();

  // Prefer persisted name; fall back to 'Guest'
  const persistedName = typeof window !== 'undefined' ? localStorage.getItem('userName') : null;
  const userName = persistedName || 'Guest';
  const persistedAdmin = typeof window !== 'undefined' ? localStorage.getItem('admin_user') : null;
  const adminUser = persistedAdmin ? JSON.parse(persistedAdmin) : null;
  const canCreateUser = hasPermission('createUser');
  const canEditUser = hasPermission('editUser');
  const canToggleStatus = hasPermission('toggleStatus');
  const canRenew = hasPermission('renew');
  const canUpdateSandboxToken = hasPermission('sandboxToken');
  const canUpdateProductionToken = hasPermission('productionToken');
  const canToggleProductionMode = hasPermission('toggleProductionMode');
  const canToggleApproval = hasPermission('toggleApproval');
  const canLoginAsClient = hasPermission('loginAsClient') || isSuperAdmin();
  const superAdmin = isSuperAdmin();

  // Data
  const [users, setUsers] = useState([]);
  const [assignableAdmins, setAssignableAdmins] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters / sorting
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [sortBy, setSortBy] = useState({ key: 'company', dir: 'asc' });

  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRenew, setShowRenew] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingLoadingId, setEditingLoadingId] = useState(null);

  // Token edit modal
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenEditType, setTokenEditType] = useState(null); // 'sandbox' | 'production'
  const [prevToken, setPrevToken] = useState('');
  const [newToken, setNewToken] = useState('');

  // Renew fields
  const [expiryDate, setExpiryDate] = useState('');
  const [amount, setAmount] = useState('');

  // Add user fields
  const [newCompany, setNewCompany] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newNtnCnic, setNewNtnCnic] = useState('');
  const [newSalesTaxId, setNewSalesTaxId] = useState('');
  const [newGstNumber, setNewGstNumber] = useState('');
  const [newProvince, setNewProvince] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newBusinessAddress, setNewBusinessAddress] = useState('');
  const [newContactPerson, setNewContactPerson] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newBusinessScenarios, setNewBusinessScenarios] = useState('');
  const [scenarioPickerOpen, setScenarioPickerOpen] = useState(false);
  const [newHsCodes, setNewHsCodes] = useState([]);
  const [showHsModal, setShowHsModal] = useState(false);
  const [hsEditIndex, setHsEditIndex] = useState(null);
  const [hsCodeDraft, setHsCodeDraft] = useState('');
  const [hsUomDraft, setHsUomDraft] = useState('');
  const [hsSaleTypeDraft, setHsSaleTypeDraft] = useState('');
  const [hsSaleTypeIdDraft, setHsSaleTypeIdDraft] = useState('');
  const [hsRateDraft, setHsRateDraft] = useState('');
  const [hsRateIdDraft, setHsRateIdDraft] = useState('');
  const [hsSroScheduleDraft, setHsSroScheduleDraft] = useState('');
  const [hsSroScheduleIdDraft, setHsSroScheduleIdDraft] = useState('');
  const [hsSroItemDraft, setHsSroItemDraft] = useState('');
  const [hsDescriptionDraft, setHsDescriptionDraft] = useState('');
  const [hsCodeSearch, setHsCodeSearch] = useState('');
  const [showHsCodePicker, setShowHsCodePicker] = useState(false);
  const [fbrHsCodes, setFbrHsCodes] = useState([]);
  const [hsCodeLoading, setHsCodeLoading] = useState(false);
  const [fbrProvinces, setFbrProvinces] = useState([]);
  const [fbrUoms, setFbrUoms] = useState([]);
  const [fbrSaleTypes, setFbrSaleTypes] = useState([]);
  const [fbrRates, setFbrRates] = useState([]);
  const [fbrSroSchedules, setFbrSroSchedules] = useState([]);
  const [fbrSroItems, setFbrSroItems] = useState([]);
  const [hsRefLoading, setHsRefLoading] = useState(false);
  const [newSandboxToken, setNewSandboxToken] = useState('');
  const [newProdToken, setNewProdToken] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [newIsApproval, setNewIsApproval] = useState(true);
  const [newInvoiceHistoryMonths, setNewInvoiceHistoryMonths] = useState(1);
  const [requiresInvoiceApproval, setRequiresInvoiceApproval] = useState(false);
  const [secondaryUserName, setSecondaryUserName] = useState('');
  const [secondaryUserEmail, setSecondaryUserEmail] = useState('');
  const [secondaryUserPhone, setSecondaryUserPhone] = useState('');
  const [secondaryUserPassword, setSecondaryUserPassword] = useState('');
  const [selectedAssignedUserIds, setSelectedAssignedUserIds] = useState([]);
  const [assignAdminDraftId, setAssignAdminDraftId] = useState('');

  // Production mode confirm modal
  const [showProdConfirm, setShowProdConfirm] = useState(false);
  const [prodTarget, setProdTarget] = useState(0); // 1 = enable production, 0 = disable
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupMonths, setBackupMonths] = useState(1);

  // Auto-dismiss toasts
  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => { setError(''); setSuccess(''); }, 3200);
      return () => clearTimeout(t);
    }
  }, [error, success]);

  // Prevent accidental swipe/back to login
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      const token = localStorage.getItem('token');
      if (token) window.history.go(1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Guard + initial load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [res, adminRes] = await Promise.all([
          getDashboard(),
          superAdmin ? getAdminUsers() : Promise.resolve(null),
        ]);
        const detail = res?.data?.response?.detail || [];
        toast.success(res?.data?.message);

        const mapped = detail.map(mapDashboardUser);
        setUsers(mapped);
        if (superAdmin && adminRes) {
          setAssignableAdmins(
            unwrapList(adminRes)
              .map(mapAssignableAdmin)
              .filter((admin) =>
                admin.id &&
                admin.status !== 'Inactive' &&
                admin.email.toLowerCase() !== SUPER_ADMIN_EMAIL
              )
          );
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const refresh = async () => {
    setReloading(true);
    try {
      const [res, adminRes] = await Promise.all([
        getDashboard(),
        superAdmin ? getAdminUsers() : Promise.resolve(null),
      ]);
      const detail = res?.data?.response?.detail || [];
      toast.success(res?.data?.message);

      const mapped = detail.map(mapDashboardUser);
      setUsers(mapped);
      if (superAdmin && adminRes) {
        setAssignableAdmins(
          unwrapList(adminRes)
            .map(mapAssignableAdmin)
            .filter((admin) =>
              admin.id &&
              admin.status !== 'Inactive' &&
              admin.email.toLowerCase() !== SUPER_ADMIN_EMAIL
            )
        );
      }
      setSuccess('Dashboard refreshed.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to refresh.');
    } finally {
      setReloading(false);
    }
  };

  // Sorting
  const setSort = (key) => {
    setSortBy(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  // Derived list
  const visible = useMemo(() => {
    let list = [...users];
    if (statusFilter !== 'all') {
      list = list.filter(u => statusFilter === 'active' ? u.status === 'Active' : u.status === 'Inactive');
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(u =>
        u.company.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const va = (a[sortBy.key] ?? '').toString().toLowerCase();
      const vb = (b[sortBy.key] ?? '').toString().toLowerCase();
      if (va < vb) return sortBy.dir === 'asc' ? -1 : 1;
      if (va > vb) return sortBy.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, statusFilter, query, sortBy]);

  // Actions
  const openConfirm = (user) => { setSelectedUser(user); setShowConfirm(true); };
  const resetUserForm = () => {
    setEditingUserId(null);
    setNewCompany(''); setNewEmail(''); setNewPassword('');
    setNewNtnCnic(''); setNewSalesTaxId(''); setNewGstNumber('');
    setNewProvince(''); setNewCity(''); setNewBusinessAddress('');
    setNewContactPerson(''); setNewPhone(''); setNewBusinessScenarios(''); setNewHsCodes([]);
    setScenarioPickerOpen(false);
    setHsCodeDraft(''); setHsUomDraft(''); setHsSaleTypeDraft(''); setHsSaleTypeIdDraft(''); setHsRateDraft(''); setHsRateIdDraft('');
    setHsSroScheduleDraft(''); setHsSroScheduleIdDraft(''); setHsSroItemDraft(''); setHsDescriptionDraft('');
    setHsCodeSearch(''); setShowHsCodePicker(false);
    setNewSandboxToken(''); setNewProdToken(''); setNewExpiry(''); setNewIsApproval(true); setNewInvoiceHistoryMonths(1);
    setRequiresInvoiceApproval(false);
    setSecondaryUserName('');
    setSecondaryUserEmail('');
    setSecondaryUserPhone('');
    setSecondaryUserPassword('');
    setSelectedAssignedUserIds([]);
    setAssignAdminDraftId('');
  };

  const openAddUser = () => {
    resetUserForm();
    setShowAdd(true);
  };

  const fillUserForm = (user) => {
    setEditingUserId(user.id);
    setNewCompany(user.company === 'Unknown' ? '' : user.company);
    setNewEmail(user.email === 'N/A' ? '' : user.email);
    setNewPassword('');
    setNewNtnCnic(user.ntn_cnic || '');
    setNewSalesTaxId(user.sales_tax_id || '');
    setNewGstNumber(user.gst_number || '');
    setNewProvince(user.province || '');
    setNewCity(user.city || '');
    setNewBusinessAddress(user.business_address || '');
    setNewContactPerson(user.contact_person || '');
    setNewPhone(user.phone || '');
    setNewBusinessScenarios((user.business_scenarios || []).map(getScenarioValue).filter(Boolean).join(', '));
    setNewHsCodes((user.hs_codes || []).map(row => ({
      hs_code: getHsRowValue(row, ['hs_code', 'hsCode', 'hS_CODE', 'HS_CODE', 'code']) || '',
      uom: getHsRowValue(row, ['uom', 'uoM', 'UOM', 'uom_desc', 'uomDesc']) || '',
      sale_type: getHsRowValue(row, ['sale_type', 'saleType', 'transaction_desc', 'transactionDesc']) || '',
      sale_type_id: getHsRowValue(row, ['sale_type_id', 'saleTypeId', 'transaction_type_id', 'transactionTypeId']) || '',
      rate: getHsRowValue(row, ['rate', 'rate_desc', 'rateDesc']) || '',
      rate_id: getHsRowValue(row, ['rate_id', 'rateId']) || '',
      sro_schedule_no: getHsRowValue(row, ['sro_schedule_no', 'sroScheduleNo', 'sro_schedule', 'sroSchedule']) || '',
      sro_schedule_id: getHsRowValue(row, ['sro_schedule_id', 'sroScheduleId', 'sro_id', 'sroId']) || '',
      sro_item_no: getHsRowValue(row, ['sro_item_no', 'sroItemNo', 'sroItemSerialNo', 'sro_item_serial_no']) || '',
      description: getHsRowValue(row, ['description', 'desc', 'hs_desc', 'hsDesc']) || null,
    })).filter(row => row.hs_code && row.uom));
    setNewSandboxToken(user.sandbox_token || '');
    setNewProdToken(user.production_token || '');
    setNewExpiry(user.expiry_date !== 'N/A' ? user.expiry_date : '');
    setNewIsApproval(Boolean(user.is_approval));
    setNewInvoiceHistoryMonths(Number(user.invoice_history_months || (user.is_invoice_history ? 60 : 1)));
    setRequiresInvoiceApproval(Boolean(user.requires_invoice_approval));
    setSecondaryUserName('');
    setSecondaryUserEmail('');
    setSecondaryUserPhone('');
    setSecondaryUserPassword('');
    setSelectedAssignedUserIds(user.assigned_user_ids || []);
    setAssignAdminDraftId('');
  };

  const openEditUser = async (user) => {
    try {
      setEditingLoadingId(user.id);
      const res = await getUserDetail(user.id);
      const detailResponse = res?.data?.response?.detail || res?.data?.response?.data;
      const detail = Array.isArray(detailResponse) ? detailResponse[0] : detailResponse;
      const fullUser = detail ? mapDashboardUser(detail) : user;
      fillUserForm(fullUser);
    } catch (err) {
      fillUserForm(user);
      setError(err?.response?.data?.message || 'Using table data because full user detail could not be loaded.');
    } finally {
      setEditingLoadingId(null);
    }
    setShowAdd(true);
  };

  const openRenew = (user) => {
    setSelectedUser(user);
    setExpiryDate(user.expiry_date !== 'N/A' ? user.expiry_date : '');
    setAmount('');
    setShowRenew(true);
  };

  // Production mode
  const openProdConfirm = (user) => {
    setSelectedUser(user);
    const next = user.is_prod_token ? 0 : 1;
    setProdTarget(next);
    setConfirmPassword('');
    setShowProdConfirm(true);
  };

  const confirmProdChange = async () => {
    if (!selectedUser) return;
    if (!confirmPassword) { setError('Please enter your password to confirm.'); return; }
    try {
   const res=   await setProductionMode(selectedUser.id, prodTarget, confirmPassword);
   toast.success(res?.data?.message);

      setUsers(prev =>
        prev.map(u => (u.id === selectedUser.id ? { ...u, is_prod_token: prodTarget } : u))
      );
      setSuccess(
        prodTarget === 1
          ? 'After enabling it your data send to production.'
          : 'After disabling it your data send to sandbox.'
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update production mode.');
    } finally {
      setShowProdConfirm(false);
      setSelectedUser(null);
      setConfirmPassword('');
    }
  };

  const closeModals = () => {
    setShowConfirm(false);
    setShowRenew(false);
    setShowAdd(false);
    setShowTokenModal(false);
    setShowProdConfirm(false);
    setShowBackupModal(false);
    setShowHsModal(false);
    setScenarioPickerOpen(false);
    setTokenEditType(null);
    setPrevToken('');
    setNewToken('');
    setConfirmPassword('');
    setBackupMonths(1);
    setEditingUserId(null);
    setHsEditIndex(null);
    setHsCodeDraft('');
    setHsUomDraft('');
    setHsSaleTypeDraft('');
    setHsSaleTypeIdDraft('');
    setHsRateDraft('');
    setHsRateIdDraft('');
    setHsSroScheduleDraft('');
    setHsSroScheduleIdDraft('');
    setHsSroItemDraft('');
    setHsDescriptionDraft('');
    setHsCodeSearch('');
    setShowHsCodePicker(false);
    setSelectedUser(null);
  };

  const toggleStatus = async () => {
    if (!selectedUser) return;
    try {
      const newStatus = selectedUser.status === 'Active' ? 0 : 1;
   const res=   await toggleUserStatus(selectedUser.id, newStatus);
   toast.success(res?.data?.message);

      setUsers(prev =>
        prev.map(u => (u.id === selectedUser.id ? { ...u, status: newStatus ? 'Active' : 'Inactive' } : u))
      );
      setSuccess(`User ${selectedUser.name} ${newStatus ? 'activated' : 'deactivated'} successfully.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update user status.');
    } finally {
      closeModals();
    }
  };

  const renew = async () => {
    if (!selectedUser || !amount || !expiryDate) { setError('Please fill all fields for renewal.'); return; }
    try {
   const res=   await rechargeUser(selectedUser.id, parseFloat(amount), expiryDate);
   toast.success(res?.data?.message);

      setUsers(prev => prev.map(u => (u.id === selectedUser.id ? { ...u, expiry_date: expiryDate } : u)));
      setSuccess(`Subscription renewed for ${selectedUser.name} until ${expiryDate}.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to renew subscription.');
    } finally {
      closeModals();
    }
  };

  const validateAddUser = () => {
    if (!newCompany || !newEmail || (!editingUserId && !newPassword) || !newNtnCnic || !newSandboxToken || !newProvince || !newCity || !newBusinessScenarios || !newExpiry) return false;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail);
    if (!emailOk) return false;
    if (!editingUserId && requiresInvoiceApproval) {
      const secondaryEmailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(secondaryUserEmail);
      return Boolean(secondaryUserName && secondaryUserEmail && secondaryUserPassword && secondaryEmailOk);
    }
    return true;
  };

  const parseBusinessScenarios = (value) =>
    value
      .split(/\r?\n|,/)
      .map(s => s.trim())
      .filter(Boolean)
      .map(scenario_id => ({ scenario_id }));

  const selectedScenarioIds = useMemo(
    () => parseBusinessScenarios(newBusinessScenarios).map((row) => row.scenario_id),
    [newBusinessScenarios]
  );

  const toggleScenarioId = (scenarioId) => {
    const current = selectedScenarioIds.includes(scenarioId)
      ? selectedScenarioIds.filter((id) => id !== scenarioId)
      : [...selectedScenarioIds, scenarioId];
    setNewBusinessScenarios(current.join(', '));
  };

  const toggleAssignedUserId = (userId) => {
    const id = String(userId);
    setSelectedAssignedUserIds((prev) =>
      prev.includes(id) ? prev.filter((currentId) => currentId !== id) : [...prev, id]
    );
  };

  const addAssignedUserId = (userId) => {
    const id = String(userId || '');
    if (!id) return;
    setSelectedAssignedUserIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setAssignAdminDraftId('');
  };

  const getAssignableAdminLabel = (userId) => {
    const admin = assignableAdmins.find((item) => String(item.id) === String(userId));
    return admin ? `${admin.name || admin.email}${admin.email ? ` (${admin.email})` : ''}` : userId;
  };

  const selectAllScenarioIds = () => {
    setNewBusinessScenarios(BUSINESS_SCENARIO_IDS.join(', '));
  };

  const clearScenarioIds = () => {
    setNewBusinessScenarios('');
  };

  const getHsLookupAuth = () => ({
    token_dev: newSandboxToken,
    token_prod: '',
    is_prod_token: 0,
  });

  const getHsLookupDate = () => new Date().toISOString().split('T')[0];

  const resetHsFbrDependencies = () => {
    setHsRateDraft('');
    setHsRateIdDraft('');
    setHsSroScheduleDraft('');
    setHsSroScheduleIdDraft('');
    setHsSroItemDraft('');
    setFbrRates([]);
    setFbrSroSchedules([]);
    setFbrSroItems([]);
  };

  const getSelectedProvinceCode = (provinceOptions = fbrProvinces) => {
    const selected = provinceOptions.find((opt) => provinceMatches(opt, newProvince));
    return getProvinceCode(selected) || newProvince;
  };

  const loadHsReferenceData = async () => {
    const currentData = {
      hsCodes: fbrHsCodes,
      provinces: fbrProvinces,
      uoms: fbrUoms,
      saleTypes: fbrSaleTypes,
    };
    if (!newSandboxToken || hsRefLoading) return currentData;
    if (fbrHsCodes.length && fbrUoms.length && fbrSaleTypes.length && fbrProvinces.length) return currentData;

    try {
      setHsRefLoading(true);
      const auth = getHsLookupAuth();
      const [hsCodeData, provinceData, uomData, saleTypeData] = await Promise.all([
        getFbrHsCodes(auth),
        getFbrProvinces(auth),
        getFbrUoms(auth),
        getFbrTransactionTypes(auth),
      ]);
      setFbrHsCodes(hsCodeData || []);
      setFbrProvinces(provinceData || []);
      setFbrUoms(uomData || []);
      setFbrSaleTypes(saleTypeData || []);
      return {
        hsCodes: hsCodeData || [],
        provinces: provinceData || [],
        uoms: uomData || [],
        saleTypes: saleTypeData || [],
      };
    } catch (err) {
      setError(err?.message || 'Failed to load FBR HS-code dropdowns.');
      return currentData;
    } finally {
      setHsRefLoading(false);
    }
  };

  const loadHsCodeOptions = async () => {
    if (!newSandboxToken) {
      setError('Enter a sandbox token first to load HS codes.');
      return [];
    }
    if (fbrHsCodes.length) return fbrHsCodes;

    try {
      setHsCodeLoading(true);
      const hsCodeData = await getFbrHsCodes(getHsLookupAuth());
      setFbrHsCodes(hsCodeData || []);
      return hsCodeData || [];
    } catch (err) {
      setError(err?.message || 'Failed to load FBR HS codes.');
      return [];
    } finally {
      setHsCodeLoading(false);
    }
  };

  const visibleHsCodeOptions = useMemo(() => {
    const term = hsCodeSearch.trim().toLowerCase();
    const filtered = term
      ? fbrHsCodes.filter((opt) => {
          const code = String(normalizeHsCode(getHsCodeValue(opt))).toLowerCase();
          const desc = String(getHsCodeDesc(opt)).toLowerCase();
          return code.includes(term) || desc.includes(term);
        })
      : fbrHsCodes;
    return filtered.slice(0, 150);
  }, [fbrHsCodes, hsCodeSearch]);

  const loadRatesForSaleType = async (saleTypeId) => {
    const refData = fbrProvinces.length ? { provinces: fbrProvinces } : await loadHsReferenceData();
    const provinceCode = getSelectedProvinceCode(refData?.provinces || fbrProvinces);
    if (!saleTypeId || !provinceCode || !newSandboxToken) return [];

    const rateData = await getFbrRates(
      {
        date: getHsLookupDate(),
        transTypeId: saleTypeId,
        originationSupplier: provinceCode,
      },
      getHsLookupAuth()
    );
    setFbrRates(rateData || []);
    return rateData || [];
  };

  const loadSroSchedulesForRate = async (rateId) => {
    const refData = fbrProvinces.length ? { provinces: fbrProvinces } : await loadHsReferenceData();
    const provinceCode = getSelectedProvinceCode(refData?.provinces || fbrProvinces);
    if (!rateId || !provinceCode || !newSandboxToken) return [];

    const sroData = await getFbrSroSchedules(
      {
        rateId,
        date: getHsLookupDate(),
        originationSupplier: provinceCode,
      },
      getHsLookupAuth()
    );
    setFbrSroSchedules(sroData || []);
    return sroData || [];
  };

  const loadSroItemsForSchedule = async (sroScheduleId) => {
    if (!sroScheduleId || !newSandboxToken) return [];

    const sroItemData = await getFbrSroItems(
      {
        date: getHsLookupDate(),
        sroId: sroScheduleId,
      },
      getHsLookupAuth()
    );
    setFbrSroItems(sroItemData || []);
    return sroItemData || [];
  };

  const openHsModal = async (index = null) => {
    const row = index !== null ? newHsCodes[index] : null;
    setHsEditIndex(index);
    setHsCodeDraft(normalizeHsCode(getHsRowValue(row, ['hs_code', 'hsCode', 'hS_CODE', 'HS_CODE', 'code'])) || '');
    setHsUomDraft(getHsRowValue(row, ['uom', 'uoM', 'UOM', 'uom_desc', 'uomDesc']) || '');
    setHsSaleTypeDraft(getHsRowValue(row, ['sale_type', 'saleType', 'transaction_desc', 'transactionDesc']) || '');
    setHsSaleTypeIdDraft(getHsRowValue(row, ['sale_type_id', 'saleTypeId', 'transaction_type_id', 'transactionTypeId']) || '');
    setHsRateDraft(getHsRowValue(row, ['rate', 'rate_desc', 'rateDesc']) || '');
    setHsRateIdDraft(getHsRowValue(row, ['rate_id', 'rateId']) || '');
    setHsSroScheduleDraft(getHsRowValue(row, ['sro_schedule_no', 'sroScheduleNo', 'sro_schedule', 'sroSchedule']) || '');
    setHsSroScheduleIdDraft(getHsRowValue(row, ['sro_schedule_id', 'sroScheduleId', 'sro_id', 'sroId']) || '');
    setHsSroItemDraft(getHsRowValue(row, ['sro_item_no', 'sroItemNo', 'sroItemSerialNo', 'sro_item_serial_no']) || '');
    setHsDescriptionDraft(getHsRowValue(row, ['description', 'desc', 'hs_desc', 'hsDesc']) || '');
    setHsCodeSearch('');
    setShowHsCodePicker(false);
    setFbrRates([]);
    setFbrSroSchedules([]);
    setFbrSroItems([]);
    setShowHsModal(true);
    await loadHsReferenceData();
  };

  const saveHsCode = () => {
    const hs_code = normalizeHsCode(hsCodeDraft);
    const uom = hsUomDraft.trim();
    const sale_type = hsSaleTypeDraft.trim();
    const sale_type_id = String(hsSaleTypeIdDraft || '').trim();
    const rate = hsRateDraft.trim();
    const rate_id = String(hsRateIdDraft || '').trim();
    const sro_schedule_no = hsSroScheduleDraft.trim();
    const sro_schedule_id = String(hsSroScheduleIdDraft || '').trim();
    const sro_item_no = hsSroItemDraft.trim();

    if (!hs_code || !uom) {
      setError('HS Code and UoM are required.');
      return;
    }
    if (!/^\d{4}\.\d{3,4}$/.test(hs_code)) {
      setError('HS Code format must be xxxx.xxx or xxxx.xxxx.');
      return;
    }
    if (!isDefaultSaleType(sale_type) && (!sro_schedule_no || !sro_item_no)) {
      setError('SRO Schedule Number and SRO Item No are required when sale type is not Goods at standard rate (default).');
      return;
    }

    const nextRow = {
      hs_code,
      uom,
      sale_type: sale_type || null,
      sale_type_id: sale_type_id || null,
      rate: rate || null,
      rate_id: rate_id || null,
      sro_schedule_no: sro_schedule_no || null,
      sro_schedule_id: sro_schedule_id || null,
      sro_item_no: sro_item_no || null,
      description: null,
    };
    setNewHsCodes((prev) => {
      if (hsEditIndex !== null) {
        return prev.map((row, index) => (index === hsEditIndex ? nextRow : row));
      }
      return [...prev, nextRow];
    });
    setShowHsModal(false);
    setHsEditIndex(null);
    setHsCodeDraft('');
    setHsUomDraft('');
    setHsSaleTypeDraft('');
    setHsSaleTypeIdDraft('');
    setHsRateDraft('');
    setHsRateIdDraft('');
    setHsSroScheduleDraft('');
    setHsSroScheduleIdDraft('');
    setHsSroItemDraft('');
    setHsDescriptionDraft('');
  };

  const deleteHsCode = (indexToDelete) => {
    setNewHsCodes((prev) => prev.filter((_, index) => index !== indexToDelete));
  };

  const addUser = async () => {
    if (!validateAddUser()) { setError('Please fill all required fields with valid data.'); return; }
    try {
      const payload = {
        ...(editingUserId ? { user_id: editingUserId } : {}),
        company_name: newCompany,
        email: newEmail,
        ...(newPassword ? { password: newPassword } : {}),
        ntn_cnic: newNtnCnic,
        sales_tax_id: newSalesTaxId || null,
        gst_number: newGstNumber || null,
        province: newProvince,
        city: newCity,
        business_address: newBusinessAddress || null,
        contact_person: newContactPerson || null,
        phone: newPhone || null,
        business_scenarios: parseBusinessScenarios(newBusinessScenarios),
        hs_codes: newHsCodes,
        sandbox_token: newSandboxToken,
        production_token: newProdToken || null,
        expiry_date: newExpiry,
        is_approval: newIsApproval ? 1 : 0,
        is_invoice_history: newInvoiceHistoryMonths > 1 ? 1 : 0,
        invoice_history_months: newInvoiceHistoryMonths,
        requires_invoice_approval: !editingUserId && requiresInvoiceApproval ? 1 : 0,
        ...(!editingUserId && requiresInvoiceApproval
          ? {
              secondary_user: {
                name: secondaryUserName,
                email: secondaryUserEmail,
                phone: secondaryUserPhone || null,
                password: secondaryUserPassword,
              },
            }
          : {}),
        ...(superAdmin
          ? {
              assigned_user_ids: selectedAssignedUserIds.map((id) =>
                /^\d+$/.test(String(id)) ? Number(id) : id
              ),
            }
          : {}),
      };
      const res = editingUserId ? await editUser(payload) : await createUser(payload);
      toast.success(res?.data?.message);

      setSuccess(editingUserId ? 'User updated successfully.' : 'User created successfully.');
      resetUserForm();
      closeModals();
      refresh();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create user.');
    }
  };

  // Token edit flow
  const openTokenEditor = (user, type) => {
    setSelectedUser(user);
    setTokenEditType(type);
    const current = type === 'sandbox' ? user.sandbox_token : user.production_token;
    setPrevToken(current || '');
    setNewToken(''); // do NOT prefill
    setShowTokenModal(true);
  };

  const saveToken = async () => {
    if (!selectedUser || !tokenEditType) return;
    if (!newToken) { setError('Please enter a new token.'); return; }
    if (newToken === prevToken) { setError('New token matches the previous token.'); return; }

    const label = tokenEditType === 'sandbox' ? 'Sandbox Token' : 'Production Token';
    const confirmed = window.confirm(`Update ${label} for ${selectedUser.name}?`);
    if (!confirmed) return;

    try {
      if (tokenEditType === 'sandbox') {
      const res=  await updateSandboxToken(selectedUser.id, newToken);
      toast.success(res?.data?.message);

        setUsers(prev => prev.map(u => (u.id === selectedUser.id ? { ...u, sandbox_token: newToken } : u)));
      } else {
    const res=    await updateProductionToken(selectedUser.id, newToken);
    toast.success(res?.data?.message);

        setUsers(prev => prev.map(u => (u.id === selectedUser.id ? { ...u, production_token: newToken } : u)));
      }
      setSuccess(`${label} updated successfully.`);
      closeModals();
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to update ${label.toLowerCase()}.`);
    }
  };

  const loginAsClient = async (user) => {
    const clientTab = window.open('about:blank', '_blank');

    if (clientTab?.document) {
      clientTab.document.write(`
        <html>
          <head><title>Opening Client Portal</title></head>
          <body style="font-family: Arial, sans-serif; padding: 24px;">
            <p>Opening client portal...</p>
          </body>
        </html>
      `);
      clientTab.document.close();
    }

    try {
      const res = await impersonateClient(user.id);
      const impersonationToken = res?.data?.response?.token;

      if (!impersonationToken) {
        throw new Error('No impersonation token received.');
      }

      const loginUrl = `${CLIENT_PORTAL_URL}/login?impersonation_token=${encodeURIComponent(impersonationToken)}`;

      if (clientTab) {
        clientTab.location.href = loginUrl;
      } else {
        window.open(loginUrl, '_blank');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to open client portal.';
      if (clientTab) {
        if (clientTab.document) {
          clientTab.document.body.innerHTML = `<p style="font-family: Arial, sans-serif; padding: 24px;">${errorMessage}</p>`;
        } else {
          clientTab.close();
        }
      }
      setError(errorMessage);
    }
  };

  const handleToggleApproval = async (user) => {
    const nextApproval = user.is_approval ? 0 : 1;

    try {
      const res = await toggleApproval(user.id, nextApproval);
      toast.success(res?.data?.message);

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? {
                ...u,
                is_approval: nextApproval,
                admin_code: res?.data?.response?.admin_code || u.admin_code,
              }
            : u
        )
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update admin approval.');
    }
  };

  const openBackupModal = (user) => {
    setSelectedUser(user);
    setBackupMonths(Number(user.invoice_history_months || (user.is_invoice_history ? 60 : 1)));
    setShowBackupModal(true);
  };

  const saveBackup = async () => {
    if (!selectedUser) return;
    const months = Number(backupMonths);
    try {
      const res = await setInvoiceBackup(selectedUser.id, months);
      toast.success(res?.data?.message);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id
            ? {
                ...u,
                invoice_history_months: months,
                is_invoice_history: months > 1 ? 1 : 0,
              }
            : u
        )
      );
      setSuccess(`Invoice backup updated for ${selectedUser.name}.`);
      closeModals();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update invoice backup.');
    }
  };

  return (
    <PageShell>
    <div className="layout">
      {/* Topbar */}
      <div className="topbar">
        <div className="brand">
          <div className="logo">⚡</div>
          <div className="meta">
            <div className="title">Admin Console</div>
            <div className="sub">User & Subscription Management</div>
          </div>
        </div>
        {/* Actions */}
        <div className="top-actions">
          <button type="button" className="btn light" onClick={refresh} disabled={reloading}>
            {reloading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="btn danger"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('userName');
              localStorage.removeItem('admin_user');
              navigate('/login', { replace: true });
            }}
          >
            Logout
          </button>
          <div className="avatar" title={userName}>{userName?.[0]?.toUpperCase() || 'U'}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="toolbar">
        <div className="search">
          <input
            type="text"
            name="userSearch"               // 👈 unique name (avoid autofill pairing)
            autoComplete="off"              // 👈 stop autofill changing query
            placeholder="Search company, name, or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="right-controls">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {canCreateUser && (
            <button type="button" className="btn primary" onClick={openAddUser}>+ Add User</button>
          )}
        </div>
      </div>

      {/* Card */}
      <div className="card">
        <div className="card-head">
          <h3>Users</h3>
          <div className="chips">
            <span className="chip">Total: {users.length}</span>
            <span className="chip ok">Shown: {visible.length}</span>
          </div>
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th onClick={() => setSort('company')}>Company {sortBy.key==='company' && (sortBy.dir==='asc'?'▲':'▼')}</th>
                <th onClick={() => setSort('name')}>Name {sortBy.key==='name' && (sortBy.dir==='asc'?'▲':'▼')}</th>
                <th onClick={() => setSort('email')}>Email {sortBy.key==='email' && (sortBy.dir==='asc'?'▲':'▼')}</th>
                <th onClick={() => setSort('status')}>Status {sortBy.key==='status' && (sortBy.dir==='asc'?'▲':'▼')}</th>
                <th onClick={() => setSort('expiry_date')}>Expiry {sortBy.key==='expiry_date' && (sortBy.dir==='asc'?'▲':'▼')}</th>
                <th>Approval</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="sk">
                    <td><div className="sk-line" /></td>
                    <td><div className="sk-line" /></td>
                    <td><div className="sk-line" /></td>
                    <td><div className="sk-badge" /></td>
                    <td><div className="sk-line short" /></td>
                    <td><div className="sk-actions" /></td>
                  </tr>
                ))
              ) : visible.length ? (
                visible.map((u) => (
                  <tr key={u.id}>
                    <td data-label="Company">{u.company}</td>
                    <td data-label="Name">{u.name}</td>
                    <td data-label="Email">{u.email}</td>
                    <td data-label="Status">
                      <span className={`badge ${u.status==='Active' ? 'ok' : 'warn'}`}>{u.status}</span>
                    </td>
                    <td data-label="Expiry">{u.expiry_date}</td>
                    <td data-label="Approval">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span className={`badge ${u.is_approval ? 'ok' : 'warn'}`}>
                          {u.is_approval ? 'Enabled' : 'Disabled'}
                        </span>
                        {u.is_approval && u.admin_code && (
                          <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '1px' }}>
                            {u.admin_code}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="actions" style={{flexWrap:'wrap', gap: '6px'}}>
                      {canToggleStatus && (
                        <button type="button" className="btn light" onClick={() => openConfirm(u)}>
                          {u.status==='Active' ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      {canEditUser && (
                        <button type="button" className="btn light" onClick={() => openEditUser(u)} disabled={editingLoadingId === u.id}>
                          {editingLoadingId === u.id ? 'Loading...' : 'Edit'}
                        </button>
                      )}
                      {canRenew && (
                        <button type="button" className="btn primary" onClick={() => openRenew(u)}>Renew</button>
                      )}
                      {adminUser && canLoginAsClient && (
                        <button type="button" className="btn primary" onClick={() => loginAsClient(u)}>Login as Client</button>
                      )}
                      {canUpdateSandboxToken && (
                        <button type="button" className="btn light" onClick={() => openTokenEditor(u, 'sandbox')}>Sandbox Token</button>
                      )}
                      {canUpdateProductionToken && (
                        <button type="button" className="btn light" onClick={() => openTokenEditor(u, 'production')}>Production Token</button>
                      )}
                      {canToggleApproval && (
                        <button type="button" className={`btn state ${u.is_approval ? 'enabled' : 'disabled'}`} onClick={() => handleToggleApproval(u)}>
                          {u.is_approval ? 'Disable Approval' : 'Enable Approval'}
                        </button>
                      )}
                      {canToggleProductionMode && (
                        <button
                          type="button"
                          className={`btn state ${u.is_prod_token ? 'enabled' : 'disabled'}`}
                          onClick={() => openProdConfirm(u)}
                          title={u.is_prod_token ? 'Disable Production' : 'Enable Production'}
                        >
                          {u.is_prod_token ? 'Disable Production' : 'Enable Production'}
                        </button>
                      )}
                      {isSuperAdmin() && (
                        <button type="button" className="btn light" onClick={() => openBackupModal(u)}>
                          Backup
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="empty">No users match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toasts */}
      <div className="toasts">
        {error && <div className="toast danger">{error}</div>}
        {success && <div className="toast ok">{success}</div>}
      </div>

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="modal">
          <div className="dialog">
            <h4>Confirm Action</h4>
            <p>Are you sure you want to <b>{selectedUser?.status==='Active'?'deactivate':'activate'}</b> <b>{selectedUser?.name}</b>?</p>
            <div className="dialog-actions">
              <button type="button" className="btn danger" onClick={toggleStatus}>Yes</button>
              <button type="button" className="btn light" onClick={closeModals}>No</button>
            </div>
          </div>
        </div>
      )}

      {/* Renew Modal */}
      {showRenew && (
        <div className="modal">
          <div className="dialog">
            <h4>Renew Subscription</h4>
            <p className="muted">{selectedUser?.name}</p>
            <div className="form-grid">
              {/* <div className="field">
                <label htmlFor="issueDate">Issue Date</label>
                <input id="issueDate" type="date" value={issueDate} onChange={(e)=>setIssueDate(e.target.value)} required />
              </div> */}
              <div className="field">
                <label htmlFor="expiryDate">Expiry Date</label>
                <input id="expiryDate" type="date" value={expiryDate} onChange={(e)=>setExpiryDate(e.target.value)} required />
              </div>
              <div className="field">
                <label htmlFor="amount">Amount</label>
                <input id="amount" type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={(e)=>setAmount(e.target.value)} required />
              </div>
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn primary" onClick={renew}>Renew</button>
              <button type="button" className="btn light" onClick={closeModals}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Token Edit Modal */}
      {showTokenModal && (
        <div className="modal">
          <div className="dialog">
            <h4>{tokenEditType === 'sandbox' ? 'Sandbox Token' : 'Production Token'}</h4>
            <div className="field" style={{marginTop: 8}}>
              <label>Previous Token</label>
              <input readOnly value={prevToken} />
            </div>
            <div className="field">
              <label>New Token</label>
              <input
                value={newToken}
                onChange={(e)=>setNewToken(e.target.value)}
                placeholder="Enter new token"
              />
              {newToken && newToken === prevToken && (
                <p className="muted tiny">New token must be different from previous.</p>
              )}
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn primary" onClick={saveToken} disabled={!newToken || newToken === prevToken}>
                Save
              </button>
              <button type="button" className="btn light" onClick={() => setShowTokenModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Enable/Disable Production Modal */}
      {showProdConfirm && (
        <div className="modal">
          <div className="dialog" autoComplete="off">
            {/* Decoy field prevents browser from autofilling search/email */}
            <input type="text" name="decoy-email" autoComplete="username" style={{position:'absolute', left:'-9999px', width:1, height:1, opacity:0}} readOnly />

            <h4>{prodTarget === 1 ? 'Enable Production' : 'Disable Production'}</h4>
            <p className="muted">
              {prodTarget === 1
                ? 'After enabling it your data send to production.'
                : 'After disabling it your data send to sandbox.'}
            </p>

            <div className="field" style={{ marginTop: 8 }}>
              <label>Confirm with your password</label>
              <input
                type="password"
                name="prodConfirmPassword"       // unique name
                autoComplete="new-password"      // 👈 stop autofill pairing
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>

            <div className="dialog-actions">
              <button type="button" className="btn primary" onClick={confirmProdChange} disabled={!confirmPassword}>
                Confirm
              </button>
              <button type="button" className="btn light" onClick={closeModals}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showBackupModal && (
        <div className="modal">
          <div className="dialog">
            <h4>Invoice Backup</h4>
            <p className="muted">{selectedUser?.name}</p>
            <div className="field" style={{ marginTop: 8 }}>
              <label htmlFor="backupMonths">Backup range</label>
              <select
                id="backupMonths"
                value={backupMonths}
                onChange={(e) => setBackupMonths(Number(e.target.value))}
              >
                {BACKUP_OPTIONS.map((option) => (
                  <option key={option.months} value={option.months}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn primary" onClick={saveBackup}>Save</button>
              <button type="button" className="btn light" onClick={closeModals}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showHsModal && (
        <div className="modal hs-modal">
          <div className="dialog">
            <h4>{hsEditIndex !== null ? 'Edit HS Code' : 'Add HS Code'}</h4>
            {!newSandboxToken && (
              <p className="muted">Enter a sandbox token first to load FBR dropdowns.</p>
            )}
            {newProvince ? (
              <p className="muted">Using selected province: {newProvince}</p>
            ) : (
              <p className="muted">Select province above before loading Rate and SRO dropdowns.</p>
            )}
            {hsRefLoading && <p className="muted">Loading FBR dropdowns...</p>}
            <div className="form-grid">
              <div className="field">
                <label htmlFor="hsCodeDraft">HS Code</label>
                <div className="lookup-field">
                  <input
                    id="hsCodeDraft"
                    type="text"
                    placeholder="0101.2100"
                    value={hsCodeDraft}
                    onFocus={() => setShowHsCodePicker(true)}
                    onChange={(e) => {
                      setHsCodeDraft(e.target.value);
                      setHsCodeSearch(e.target.value);
                      setShowHsCodePicker(true);
                    }}
                  />
                  <button
                    type="button"
                    className="mini-btn"
                    onClick={async () => {
                      setHsCodeSearch('');
                      setShowHsCodePicker(true);
                      await loadHsCodeOptions();
                    }}
                  >
                    All
                  </button>
                  {showHsCodePicker && (
                    <div className="lookup-panel">
                      <input
                        type="text"
                        placeholder="Search HS code or description"
                        value={hsCodeSearch}
                        onChange={(e) => setHsCodeSearch(e.target.value)}
                      />
                      <div className="lookup-list">
                        {visibleHsCodeOptions.map((opt) => {
                          const code = normalizeHsCode(getHsCodeValue(opt));
                          const desc = getHsCodeDesc(opt);
                          return (
                            <button
                              type="button"
                              className="lookup-option"
                              key={`${code}-${desc}`}
                              onClick={() => {
                                setHsCodeDraft(code);
                                setHsDescriptionDraft('');
                                setShowHsCodePicker(false);
                              }}
                            >
                              <strong>{code}</strong>
                              {desc && <span>{desc}</span>}
                            </button>
                          );
                        })}
                        {(hsCodeLoading || hsRefLoading) && (
                          <div className="lookup-empty">Loading HS codes...</div>
                        )}
                        {!hsCodeLoading && !hsRefLoading && !visibleHsCodeOptions.length && (
                          <div className="lookup-empty">
                            {newSandboxToken ? 'No HS codes found. Click All to reload.' : 'Enter a sandbox token first.'}
                          </div>
                        )}
                      </div>
                      <div className="lookup-actions">
                        <button type="button" className="mini-btn" onClick={loadHsCodeOptions}>Reload</button>
                        <button type="button" className="mini-btn" onClick={() => setShowHsCodePicker(false)}>Close</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="field">
                <label htmlFor="hsUomDraft">UoM</label>
                <select id="hsUomDraft" value={hsUomDraft} onFocus={loadHsReferenceData} onChange={(e)=>setHsUomDraft(e.target.value)}>
                  <option value="">Select UoM</option>
                  {hsUomDraft && !fbrUoms.some((opt) => sameText(getUomDesc(opt), hsUomDraft)) && (
                    <option value={hsUomDraft}>{hsUomDraft}</option>
                  )}
                  {fbrUoms.map((opt) => (
                    <option key={pick(opt, ['uoM_ID', 'uomId', 'id']) || getUomDesc(opt)} value={getUomDesc(opt)}>
                      {getUomDesc(opt)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="hsSaleTypeDraft">Sale Type</label>
                <select
                  id="hsSaleTypeDraft"
                  value={hsSaleTypeDraft}
                  onFocus={loadHsReferenceData}
                  onChange={async (e) => {
                    const value = e.target.value;
                    const selected = fbrSaleTypes.find((opt) => sameText(getSaleTypeDesc(opt), value));
                    const saleTypeId = getSaleTypeId(selected);
                    setHsSaleTypeDraft(value);
                    setHsSaleTypeIdDraft(saleTypeId);
                    setHsRateDraft('');
                    setHsRateIdDraft('');
                    setHsSroScheduleDraft('');
                    setHsSroScheduleIdDraft('');
                    setHsSroItemDraft('');
                    setFbrRates([]);
                    setFbrSroSchedules([]);
                    setFbrSroItems([]);
                    if (saleTypeId) {
                      try {
                        await loadRatesForSaleType(saleTypeId);
                      } catch (err) {
                        setError(err?.message || 'Failed to load FBR rates.');
                      }
                    }
                  }}
                >
                  <option value="">Select Sale Type</option>
                  {hsSaleTypeDraft && !fbrSaleTypes.some((opt) => sameText(getSaleTypeDesc(opt), hsSaleTypeDraft)) && (
                    <option value={hsSaleTypeDraft}>{hsSaleTypeDraft}</option>
                  )}
                  {fbrSaleTypes.map((opt) => (
                    <option key={getSaleTypeId(opt)} value={getSaleTypeDesc(opt)}>
                      {getSaleTypeDesc(opt)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="hsRateDraft">Rate</label>
                <select
                  id="hsRateDraft"
                  value={hsRateDraft}
                  disabled={!hsSaleTypeIdDraft}
                  onFocus={() => {
                    if (!fbrRates.length && hsSaleTypeIdDraft) loadRatesForSaleType(hsSaleTypeIdDraft);
                  }}
                  onChange={async (e) => {
                    const value = e.target.value;
                    const selected = fbrRates.find((opt) => sameText(getRateDesc(opt), value));
                    const rateId = getRateId(selected);
                    setHsRateDraft(value);
                    setHsRateIdDraft(rateId);
                    setHsSroScheduleDraft('');
                    setHsSroScheduleIdDraft('');
                    setHsSroItemDraft('');
                    setFbrSroSchedules([]);
                    setFbrSroItems([]);
                    if (rateId) {
                      try {
                        await loadSroSchedulesForRate(rateId);
                      } catch (err) {
                        setError(err?.message || 'Failed to load FBR SRO schedules.');
                      }
                    }
                  }}
                >
                  <option value="">Select Rate</option>
                  {hsRateDraft && !fbrRates.some((opt) => sameText(getRateDesc(opt), hsRateDraft)) && (
                    <option value={hsRateDraft}>{hsRateDraft}</option>
                  )}
                  {fbrRates.map((opt) => (
                    <option key={getRateId(opt)} value={getRateDesc(opt)}>
                      {getRateDesc(opt)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="hsSroScheduleDraft">SRO Schedule Number</label>
                <select
                  id="hsSroScheduleDraft"
                  value={hsSroScheduleDraft}
                  disabled={!hsRateIdDraft}
                  onFocus={() => {
                    if (!fbrSroSchedules.length && hsRateIdDraft) loadSroSchedulesForRate(hsRateIdDraft);
                  }}
                  onChange={async (e) => {
                    const value = e.target.value;
                    const selected = fbrSroSchedules.find((opt) => sameText(getSroDesc(opt), value));
                    const sroId = getSroId(selected);
                    setHsSroScheduleDraft(value);
                    setHsSroScheduleIdDraft(sroId);
                    setHsSroItemDraft('');
                    setFbrSroItems([]);
                    if (sroId) {
                      try {
                        await loadSroItemsForSchedule(sroId);
                      } catch (err) {
                        setError(err?.message || 'Failed to load FBR SRO items.');
                      }
                    }
                  }}
                >
                  <option value="">No SRO Schedule</option>
                  {hsSroScheduleDraft && !fbrSroSchedules.some((opt) => sameText(getSroDesc(opt), hsSroScheduleDraft)) && (
                    <option value={hsSroScheduleDraft}>{hsSroScheduleDraft}</option>
                  )}
                  {fbrSroSchedules.map((opt) => (
                    <option key={getSroId(opt)} value={getSroDesc(opt)}>
                      {getSroDesc(opt)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="hsSroItemDraft">SRO Item No</label>
                <select
                  id="hsSroItemDraft"
                  value={hsSroItemDraft}
                  disabled={!hsSroScheduleIdDraft}
                  onFocus={() => {
                    if (!fbrSroItems.length && hsSroScheduleIdDraft) loadSroItemsForSchedule(hsSroScheduleIdDraft);
                  }}
                  onChange={(e) => setHsSroItemDraft(e.target.value)}
                >
                  <option value="">No SRO Item</option>
                  {hsSroItemDraft && !fbrSroItems.some((opt) => sameText(getSroItemDesc(opt), hsSroItemDraft)) && (
                    <option value={hsSroItemDraft}>{hsSroItemDraft}</option>
                  )}
                  {fbrSroItems.map((opt) => (
                    <option key={getSroItemId(opt)} value={getSroItemDesc(opt)}>
                      {getSroItemDesc(opt)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field full">
                <label htmlFor="hsDescriptionDraft">Description <span className="dim">(optional)</span></label>
                <textarea id="hsDescriptionDraft" placeholder="Description" value={hsDescriptionDraft} onChange={(e)=>setHsDescriptionDraft(e.target.value)} rows={3} />
              </div>
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn primary" onClick={saveHsCode}>Save</button>
              <button type="button" className="btn light" onClick={() => setShowHsModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal">
          <div className="dialog add-user-dialog">
            <div className="dialog-head">
              <div>
                <h4>{editingUserId ? 'Edit User' : 'Add User'}</h4>
                <p className="muted">{editingUserId ? 'Update client account and onboarding details.' : 'Create a new client account. All fields are required except the production token.'}</p>
              </div>
              <button type="button" className="dialog-close" onClick={closeModals} aria-label="Close dialog">x</button>
            </div>
            <div className="dialog-body">
              <p className="dialog-section-title">Client Details</p>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="company">Company Name</label>
                  <input id="company" type="text" placeholder="Enter company name" value={newCompany} onChange={(e)=>setNewCompany(e.target.value)} required />
                </div>
                <div className="field">
                  <label htmlFor="sandbox">Sandbox Token</label>
                  <input id="sandbox" type="text" placeholder="Paste sandbox token" value={newSandboxToken} onChange={(e)=>setNewSandboxToken(e.target.value)} required />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" placeholder="client@company.com" value={newEmail} onChange={(e)=>setNewEmail(e.target.value)} required />
                </div>
                <div className="field">
                  <label htmlFor="password">Password {editingUserId && <span className="dim">(leave blank to keep current)</span>}</label>
                  <input id="password" type="text" placeholder={editingUserId ? 'Enter new password' : 'Enter password'} value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} required={!editingUserId} />
                </div>
                <div className="field">
                  <label htmlFor="ntnCnic">NTN/CNIC</label>
                  <input id="ntnCnic" type="text" placeholder="Registered NTN/CNIC" value={newNtnCnic} onChange={(e)=>setNewNtnCnic(e.target.value)} required />
                </div>
                <div className="field">
                  <label htmlFor="salesTaxId">Sales Tax Registration No. <span className="dim">(optional)</span></label>
                  <input id="salesTaxId" type="text" placeholder="Sales tax registration no." value={newSalesTaxId} onChange={(e)=>setNewSalesTaxId(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="gstNumber">GST Number <span className="dim">(optional)</span></label>
                  <input id="gstNumber" type="text" placeholder="GST number" value={newGstNumber} onChange={(e)=>setNewGstNumber(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="province">Province</label>
                  <select
                    id="province"
                    value={newProvince}
                    onChange={(e) => {
                      setNewProvince(e.target.value);
                      resetHsFbrDependencies();
                    }}
                    required
                  >
                    <option value="">Select province</option>
                    {PROVINCES.map((province) => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="city">City</label>
                  <input id="city" type="text" placeholder="City" value={newCity} onChange={(e)=>setNewCity(e.target.value)} required />
                </div>
                <div className="field">
                  <label htmlFor="phone">Phone <span className="dim">(optional)</span></label>
                  <input id="phone" type="text" placeholder="Phone number" value={newPhone} onChange={(e)=>setNewPhone(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="contactPerson">Contact Person <span className="dim">(optional)</span></label>
                  <input id="contactPerson" type="text" placeholder="Contact person" value={newContactPerson} onChange={(e)=>setNewContactPerson(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="businessAddress">Business Address <span className="dim">(optional)</span></label>
                  <input id="businessAddress" type="text" placeholder="Business address" value={newBusinessAddress} onChange={(e)=>setNewBusinessAddress(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="businessScenarios">Business Scenarios</label>
                  <div className="scenario-picker">
                    <input
                      id="businessScenarios"
                      type="text"
                      placeholder="SN001, SN002"
                      value={newBusinessScenarios}
                      onFocus={() => setScenarioPickerOpen(true)}
                      onChange={(e)=>setNewBusinessScenarios(e.target.value)}
                      required
                    />
                    <button type="button" className="mini-btn" onClick={() => setScenarioPickerOpen((open) => !open)}>
                      Pick
                    </button>
                    {scenarioPickerOpen && (
                      <div className="scenario-panel">
                        <div className="scenario-toolbar">
                          <button type="button" className="mini-btn" onClick={selectAllScenarioIds}>All</button>
                          <button type="button" className="mini-btn" onClick={clearScenarioIds}>Clear</button>
                          <button type="button" className="mini-btn" onClick={() => setScenarioPickerOpen(false)}>Done</button>
                        </div>
                        <div className="scenario-options">
                          {BUSINESS_SCENARIO_IDS.map((scenarioId) => {
                            const checked = selectedScenarioIds.includes(scenarioId);
                            return (
                              <label key={scenarioId} className={`scenario-option ${checked ? 'checked' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleScenarioId(scenarioId)}
                                />
                                <span className="scenario-check">{checked ? '✓' : ''}</span>
                                <span>{scenarioId}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="field">
                  <div className="field-title-row">
                    <label>HS Codes with UoM <span className="dim">(optional)</span></label>
                    <button type="button" className="icon-btn" onClick={() => openHsModal()} aria-label="Add HS Code">+</button>
                  </div>
                  <div className="hs-code-list">
                    {newHsCodes.length ? (
                      newHsCodes.map((row, index) => (
                        <div className="hs-code-row" key={`${row.hs_code}-${index}`}>
                          <div className="hs-code-meta">
                            <div className="hs-code-main">
                              <strong>{row.hs_code}</strong>
                              <span>{row.uom}</span>
                            </div>
                            {(row.sale_type || row.rate || row.sro_schedule_no || row.sro_item_no) && (
                              <small>
                                {[row.sale_type, row.rate, row.sro_schedule_no, row.sro_item_no].filter(Boolean).join(' | ')}
                              </small>
                            )}
                            {row.description && <small>{row.description}</small>}
                          </div>
                          <div className="hs-code-actions">
                            <button type="button" className="mini-btn" onClick={() => openHsModal(index)}>Edit</button>
                            <button type="button" className="mini-btn danger" onClick={() => deleteHsCode(index)}>Delete</button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="hs-empty">No HS codes added.</div>
                    )}
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="prod">Production Token <span className="dim">(optional)</span></label>
                  <input id="prod" type="text" placeholder="Paste production token" value={newProdToken} onChange={(e)=>setNewProdToken(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="expiry">Expiry Date</label>
                  <input id="expiry" type="date" value={newExpiry} onChange={(e)=>setNewExpiry(e.target.value)} required />
                </div>
                {superAdmin && (
                  <div className="field full">
                    <div className="field-title-row">
                      <label>Assign Client To Users <span className="dim">(optional)</span></label>
                      {selectedAssignedUserIds.length > 0 && (
                        <button type="button" className="mini-btn" onClick={() => setSelectedAssignedUserIds([])}>
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="assign-user-dropdown">
                      <select
                        value={assignAdminDraftId}
                        onChange={(event) => {
                          setAssignAdminDraftId(event.target.value);
                          addAssignedUserId(event.target.value);
                        }}
                        disabled={!assignableAdmins.length}
                      >
                        <option value="">
                          {assignableAdmins.length ? 'Select user to assign...' : 'No users available to assign'}
                        </option>
                        {assignableAdmins
                          .filter((admin) => !selectedAssignedUserIds.includes(String(admin.id)))
                          .map((admin) => (
                            <option key={admin.id} value={admin.id}>
                              {admin.name || admin.email}{admin.email ? ` - ${admin.email}` : ''}
                            </option>
                          ))}
                      </select>
                      {selectedAssignedUserIds.length > 0 && (
                        <div className="assigned-user-chips">
                          {selectedAssignedUserIds.map((id) => {
                            const label = getAssignableAdminLabel(id);
                            return (
                              <span key={id} className="assigned-user-chip">
                                {label}
                                <button type="button" onClick={() => toggleAssignedUserId(id)} aria-label={`Remove ${label}`}>x</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {!editingUserId && (
                <div className="checkbox-panel">
                  <input
                    id="requiresInvoiceApproval"
                    type="checkbox"
                    checked={requiresInvoiceApproval}
                    onChange={(e) => setRequiresInvoiceApproval(e.target.checked)}
                  />
                  <div className="checkbox-content">
                    <label htmlFor="requiresInvoiceApproval">Require Second User Invoice Approval</label>
                    <p className="checkbox-copy">
                      Create a linked non-admin login for this company. This user can validate invoices, while the admin user reviews and submits them.
                    </p>
                  </div>
                </div>
              )}
              {!editingUserId && requiresInvoiceApproval && (
                <div className="field full pra-section">
                  <p className="dialog-section-title">Second User Details</p>
                  <div className="form-grid nested">
                    <div className="field">
                      <label htmlFor="secondaryUserName">Name</label>
                      <input
                        id="secondaryUserName"
                        type="text"
                        placeholder="Second user name"
                        value={secondaryUserName}
                        onChange={(e) => setSecondaryUserName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="secondaryUserEmail">Email</label>
                      <input
                        id="secondaryUserEmail"
                        type="email"
                        placeholder="second.user@company.com"
                        value={secondaryUserEmail}
                        onChange={(e) => setSecondaryUserEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="secondaryUserPhone">Phone <span className="dim">(optional)</span></label>
                      <input
                        id="secondaryUserPhone"
                        type="text"
                        placeholder="Phone number"
                        value={secondaryUserPhone}
                        onChange={(e) => setSecondaryUserPhone(e.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="secondaryUserPassword">Password</label>
                      <input
                        id="secondaryUserPassword"
                        type="text"
                        placeholder="Enter password"
                        value={secondaryUserPassword}
                        onChange={(e) => setSecondaryUserPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="checkbox-panel">
                <input
                  id="adminApproval"
                  type="checkbox"
                  checked={newIsApproval}
                  onChange={(e) => setNewIsApproval(e.target.checked)}
                />
                <div className="checkbox-content">
                  <label htmlFor="adminApproval">Enable Admin Approval</label>
                  <p className="checkbox-copy">
                    Generate a 6-digit approval code for this client. Use this only when invoices outside the allowed month range should require admin verification.
                  </p>
                </div>
              </div>
              <div className="dialog-actions spacious">
                <button type="button" className="btn primary" onClick={addUser}>{editingUserId ? 'Update' : 'Submit'}</button>
                <button type="button" className="btn light" onClick={closeModals}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />

    </div>
    </PageShell>
  );
};

export default Home;
